"use client";

import { Plus } from "lucide-react";
import { addItem } from "@/components/cart/actions";
import LoadingDots from "@/components/loading-dots";
import { ProductVariant } from "@/lib/medusa/types";
import { useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";
import { useToast } from "@/components/toast-context";

export function AddToCart({ variants }: { variants: ProductVariant[] }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const { setToastState } = useToast();

  const variant =
    variants.length === 1
      ? variants[0]
      : variants.find((variant: ProductVariant) =>
          variant.selectedOptions.every(
            (option) =>
              option.value === searchParams?.get(option.name.toLowerCase())
          )
        );

  const availableForSale =
    variant?.inventory_quantity || 0 > 0 || variant?.allow_backorder === true;
  const selectedVariantId = variant?.id;

  // Button labels translated to Hungarian
  const buttonLabel = !selectedVariantId
    ? "Válasszon az opciók közül"
    : !availableForSale
    ? "Nincs készleten"
    : "Kosárba";

  return (
    <button
      disabled={isPending || !availableForSale || !selectedVariantId}
      onClick={() => {
        // Safeguard in case someone messes with `disabled` in devtools.
        if (!availableForSale || !selectedVariantId) return;

        startTransition(async () => {
          const error = await addItem(selectedVariantId);

          if (error) {
            setToastState({ open: true, message: error, type: "error" });
          }

          router.refresh();
        });
      }}
      className="relative flex disabled:opacity-60 disabled:cursor-not-allowed w-full items-center justify-center rounded-full bg-blue-600 p-4 tracking-wide text-white hover:opacity-90"
    >
      <div className="absolute left-0 ml-4">
        {!isPending ? (
          <Plus className="h-5" />
        ) : (
          <LoadingDots className="mb-3 bg-white" />
        )}
      </div>
      <span>{buttonLabel}</span>
    </button>
  );
}
